import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import { fileStoreApi, layoutApi } from '../api';
import { LayoutInfo } from '../types';
import { EditorState, LayoutDefinition, LayoutKind, Widget } from '../layout/types';
import { createDefaultLayout, slugifyLayoutName } from '../layout/createDefaultLayout';
import { WidgetToolbox } from '../layout/components/WidgetToolbox';
import { PreviewPanel } from '../layout/components/PreviewPanel';
import { PropertyPanel } from '../layout/components/PropertyPanel';
import { ObjectList } from '../layout/components/ObjectList';
import {
  absoluteOrigin,
  applyKind,
  findDropContainer,
  getWidgetAt,
  nextWidgetId,
  normalizeLayout,
  reparentWidget,
  serializeLayout,
  toolboxTypesFor,
  widgetsOf,
  withWidgets,
  setWidgetAt,
  removeWidgetAt,
} from '../layout/normalize';
import { ConfigSchema, defaultsFromFields, schemaItemTypes } from '../schema/schema';
import { useI18n } from '../i18n';

const LayoutEditor: React.FC = () => {
  const { t } = useI18n();
  const [editorState, setEditorState] = useState<EditorState>(() => ({
    layout: createDefaultLayout('simple'),
    selectedWidget: undefined,
    selectedWidgetPath: undefined,
    zoom: 1,
    isDirty: false,
  }));
  const [serverLayouts, setServerLayouts] = useState<LayoutInfo[]>([]);
  const [savedName, setSavedName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [layoutSchema, setLayoutSchema] = useState<ConfigSchema | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const notify = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({ message, severity });
  };

  const refreshServerLayouts = useCallback(async () => {
    try {
      const layouts = await layoutApi.list();
      setServerLayouts(layouts);
    } catch (err: any) {
      notify(err?.response?.data?.error || err?.message || t('layout.listFailed'), 'error');
    }
  }, [t]);

  useEffect(() => {
    refreshServerLayouts();
  }, [refreshServerLayouts]);

  useEffect(() => {
    fileStoreApi.getFileSchema('layout').then(setLayoutSchema).catch(() => setLayoutSchema(null));
  }, []);

  const handleLayoutChange = useCallback((layout: LayoutDefinition) => {
    setEditorState((prev) => ({
      ...prev,
      layout,
      isDirty: true,
    }));
  }, []);

  const applyLoadedLayout = useCallback((layout: LayoutDefinition, name: string | null) => {
    const next = normalizeLayout(layout);
    setSavedName(name);
    setEditorState({
      layout: next,
      selectedWidget: undefined,
      selectedWidgetPath: undefined,
      zoom: 1,
      isDirty: false,
    });
  }, []);

  const confirmDiscard = useCallback(() => {
    if (!editorState.isDirty) {
      return true;
    }
    return window.confirm(t('layout.unsavedConfirm'));
  }, [editorState.isDirty, t]);

  const widgetItemTypes = schemaItemTypes(layoutSchema, 'widgets', 'widgets');
  const allowedTypes = toolboxTypesFor(editorState.layout.kind);
  const widgets = widgetsOf(editorState.layout);
  const hasSelection = Boolean(editorState.selectedWidgetPath?.length);

  const handleWidgetAdd = useCallback((widgetType: string) => {
    const spec = widgetItemTypes[widgetType];
    const defaults = spec ? defaultsFromFields(spec.fields) : {};
    setEditorState((prev) => {
      const existing = widgetsOf(prev.layout);
      const width = Number(defaults.width) || 80;
      const height = Number(defaults.height) || 40;
      const empty = existing.length === 0;
      const newWidget: Widget = {
        ...defaults,
        id: String(defaults.id || '').trim() || nextWidgetId(existing, widgetType),
        type: widgetType,
        x: empty
          ? Math.max(0, Math.floor((prev.layout.display.width - width) / 2))
          : Number(defaults.x) || 50,
        y: empty
          ? Math.max(0, Math.floor((prev.layout.display.height - height) / 2))
          : Number(defaults.y) || 50,
        width,
        height,
      };
      const nextWidgets = [...existing, newWidget];
      return {
        ...prev,
        layout: withWidgets(prev.layout, nextWidgets),
        selectedWidget: newWidget,
        selectedWidgetPath: [nextWidgets.length - 1],
        isDirty: true,
      };
    });
  }, [widgetItemTypes]);

  const handleWidgetChange = useCallback((widget: Widget) => {
    setEditorState((prev) => {
      const path = prev.selectedWidgetPath;
      if (!path || !path.length) {
        return prev;
      }
      const nextWidgets = setWidgetAt(widgetsOf(prev.layout), path, widget);
      return {
        ...prev,
        layout: withWidgets(prev.layout, nextWidgets),
        selectedWidget: widget,
        isDirty: true,
      };
    });
  }, []);

  const handleSaveServer = useCallback(async () => {
    const payload = serializeLayout(editorState.layout);
    const name = savedName || slugifyLayoutName(payload.metadata.name);
    try {
      setBusy(true);
      await layoutApi.save(name, payload);
      setSavedName(name);
      setEditorState((prev) => ({ ...prev, layout: payload, isDirty: false }));
      await refreshServerLayouts();
      notify(t('layout.saved', { name: payload.metadata.name }), 'success');
    } catch (err: any) {
      notify(err?.response?.data?.error || err?.message || t('layout.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  }, [editorState.layout, refreshServerLayouts, savedName, t]);

  const handleLoadServer = useCallback(async (name: string) => {
    if (!name) {
      return;
    }
    if (!confirmDiscard()) {
      return;
    }
    try {
      setBusy(true);
      const layout = await layoutApi.get(name);
      applyLoadedLayout(layout, name);
      notify(t('layout.loaded', { name: layout.metadata?.name || name }), 'success');
    } catch (err: any) {
      notify(err?.response?.data?.error || err?.message || t('layout.loadFailed'), 'error');
    } finally {
      setBusy(false);
    }
  }, [applyLoadedLayout, confirmDiscard, t]);

  const handleNewLayout = useCallback(() => {
    if (!confirmDiscard()) {
      return;
    }
    applyLoadedLayout(createDefaultLayout(editorState.layout.kind), null);
    notify(t('layout.created'), 'info');
  }, [applyLoadedLayout, confirmDiscard, editorState.layout.kind, t]);

  const handleDeleteLayout = useCallback(async () => {
    if (!savedName) {
      applyLoadedLayout(createDefaultLayout(editorState.layout.kind), null);
      return;
    }
    if (!window.confirm(t('layout.deleteConfirm', { name: editorState.layout.metadata.name }))) {
      return;
    }
    try {
      setBusy(true);
      await layoutApi.remove(savedName);
      await refreshServerLayouts();
      applyLoadedLayout(createDefaultLayout(editorState.layout.kind), null);
      notify(t('layout.deleted'), 'info');
    } catch (err: any) {
      notify(err?.response?.data?.error || err?.message || t('layout.deleteFailed'), 'error');
    } finally {
      setBusy(false);
    }
  }, [applyLoadedLayout, editorState.layout.kind, editorState.layout.metadata.name, refreshServerLayouts, savedName, t]);

  const handleKindChange = useCallback((kind: LayoutKind) => {
    setEditorState((prev) => ({
      ...prev,
      layout: applyKind(prev.layout, kind),
      selectedWidget: undefined,
      selectedWidgetPath: undefined,
      isDirty: true,
    }));
  }, []);

  const handleWidgetSelect = useCallback((widget: Widget, path: number[]) => {
    if (!path.length) {
      setEditorState((prev) => ({
        ...prev,
        selectedWidget: undefined,
        selectedWidgetPath: undefined,
      }));
      return;
    }
    setEditorState((prev) => ({
      ...prev,
      selectedWidget: widget,
      selectedWidgetPath: path,
    }));
  }, []);

  const handleWidgetDelete = useCallback((path: number[]) => {
    setEditorState((prev) => {
      const nextWidgets = removeWidgetAt(widgetsOf(prev.layout), path);
      return {
        ...prev,
        layout: withWidgets(prev.layout, nextWidgets),
        selectedWidget: undefined,
        selectedWidgetPath: undefined,
        isDirty: true,
      };
    });
    notify(t('layout.widgetDeleted'), 'info');
  }, [t]);

  const handleWidgetDrag = useCallback((updatedWidget: Widget, path: number[]) => {
    setEditorState((prev) => {
      const nextWidgets = setWidgetAt(widgetsOf(prev.layout), path, updatedWidget);
      return {
        ...prev,
        layout: withWidgets(prev.layout, nextWidgets),
        selectedWidget: updatedWidget,
        selectedWidgetPath: path,
        isDirty: true,
      };
    });
  }, []);

  /** Drop onto a container → reparent into its children; otherwise stay/move to root. */
  const handleWidgetDrop = useCallback((path: number[], absX: number, absY: number) => {
    setEditorState((prev) => {
      const widgets = widgetsOf(prev.layout);
      const moving = getWidgetAt(widgets, path);
      if (!moving) {
        return prev;
      }
      const ww = Number(moving.width) || 80;
      const wh = Number(moving.height) || 40;
      // Hit-test at widget center so a partial overlap still parents into the container
      const hitX = absX + Math.floor(ww / 2);
      const hitY = absY + Math.floor(wh / 2);
      const containerPath = findDropContainer(widgets, hitX, hitY, path);
      const origin = containerPath
        ? absoluteOrigin(widgets, containerPath)
        : { x: 0, y: 0 };
      const { widgets: nextWidgets, newPath } = reparentWidget(
        widgets,
        path,
        containerPath,
        absX - origin.x,
        absY - origin.y,
      );
      const selected = getWidgetAt(nextWidgets, newPath);
      return {
        ...prev,
        layout: withWidgets(prev.layout, nextWidgets),
        selectedWidget: selected,
        selectedWidgetPath: newPath,
        isDirty: true,
      };
    });
  }, []);

  const openValue = serverLayouts.some((item) => item.name === savedName) ? savedName || '' : '';
  const primaryAddType = allowedTypes.includes('label') ? 'label' : allowedTypes[0];

  const emptyHint = (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 2,
        textAlign: 'center',
        pointerEvents: 'none',
        bgcolor: 'rgba(255,255,255,0.55)',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('layout.emptyCanvas')}
      </Typography>
      {primaryAddType && (
        <Button
          size="small"
          variant="contained"
          sx={{ pointerEvents: 'auto' }}
          onClick={() => handleWidgetAdd(primaryAddType)}
        >
          {t('layout.emptyAdd', {
            type: t(`layout.widget.${primaryAddType}`),
          })}
        </Button>
      )}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {editorState.isDirty && (
            <Typography variant="body2" color="warning.main">*</Typography>
          )}
          <TextField
            size="small"
            label={t('layout.name')}
            value={editorState.layout.metadata.name}
            onChange={(e) => handleLayoutChange({
              ...editorState.layout,
              metadata: { ...editorState.layout.metadata, name: e.target.value },
            })}
            sx={{ width: 180 }}
          />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>{t('layout.kind')}</InputLabel>
            <Select
              label={t('layout.kind')}
              value={editorState.layout.kind}
              onChange={(e) => handleKindChange(e.target.value as LayoutKind)}
            >
              <MenuItem value="simple">{t('layout.kindSimple')}</MenuItem>
              <MenuItem value="lvgl">{t('layout.kindLvgl')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('layout.openSaved')}</InputLabel>
            <Select
              label={t('layout.openSaved')}
              value={openValue}
              onChange={(e) => handleLoadServer(String(e.target.value))}
            >
              <MenuItem value="">
                <em>{t('layout.openSaved')}</em>
              </MenuItem>
              {serverLayouts.map((layout) => (
                <MenuItem key={layout.name} value={layout.name}>
                  {layout.title || layout.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem />
          <Button size="small" startIcon={<Add />} onClick={handleNewLayout}>{t('common.newItem')}</Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Save />}
            disabled={busy}
            onClick={handleSaveServer}
          >
            {t('common.save')}
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<Delete />}
            disabled={busy}
            onClick={handleDeleteLayout}
          >
            {t('common.delete')}
          </Button>
        </Box>
      </Box>

      <Box display="flex" gap={1.5} sx={{ flex: 1, minHeight: 0, p: 1.5 }}>
        <Box
          sx={{
            width: 240,
            flexShrink: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ flexShrink: 0, overflow: 'visible' }}>
            <WidgetToolbox
              itemTypes={widgetItemTypes}
              allowedTypes={allowedTypes}
              onWidgetAdd={handleWidgetAdd}
              compact
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ObjectList
              layout={editorState.layout}
              selectedWidget={editorState.selectedWidget}
              selectedWidgetPath={editorState.selectedWidgetPath}
              onWidgetSelect={handleWidgetSelect}
              onWidgetDelete={handleWidgetDelete}
              compact
            />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <PreviewPanel
            layout={editorState.layout}
            zoom={editorState.zoom}
            selectedWidget={editorState.selectedWidget}
            selectedWidgetPath={editorState.selectedWidgetPath}
            onWidgetSelect={handleWidgetSelect}
            onWidgetChange={handleWidgetDrag}
            onWidgetDrop={handleWidgetDrop}
            emptyHint={widgets.length === 0 ? emptyHint : undefined}
          />
        </Box>

        <Box
          sx={{
            width: hasSelection ? 280 : 0,
            flexShrink: 0,
            minHeight: 0,
            overflow: 'hidden',
            transition: 'width 0.15s ease',
          }}
        >
          {hasSelection && (
            <PropertyPanel
              widget={editorState.selectedWidget}
              itemTypes={widgetItemTypes}
              onWidgetChange={handleWidgetChange}
              onWidgetDelete={editorState.selectedWidgetPath
                ? () => handleWidgetDelete(editorState.selectedWidgetPath as number[])
                : undefined}
            />
          )}
        </Box>
      </Box>

      <Snackbar
        open={notification !== null}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
      >
        <Alert severity={notification?.severity || 'info'} onClose={() => setNotification(null)}>
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LayoutEditor;
